import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { referralPartnerApi } from '@/api/referralPartner';
import PartnerHospitalSelect from '@/components/partner/PartnerHospitalSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

export default function PartnerReferrals() {
  const qc = useQueryClient();
  const [hospitalId, setHospitalId] = useState('');
  const [filterHospitalId, setFilterHospitalId] = useState('all');
  const [patientName, setPatientName] = useState('');
  const [phone, setPhone] = useState('');
  const [specialisation, setSpecialisation] = useState('');
  const [notes, setNotes] = useState('');
  const [consent, setConsent] = useState(false);

  const { data: leads, isLoading } = useQuery({
    queryKey: ['partner-leads', filterHospitalId],
    queryFn: async () => {
      const res = await referralPartnerApi.leads(
        filterHospitalId === 'all' ? undefined : filterHospitalId
      );
      return res.data?.data ?? [];
    },
  });

  const createMut = useMutation({
    mutationFn: () => {
      if (!hospitalId) throw new Error('Select a hospital');
      return referralPartnerApi.createLead({
        hospitalId,
        patientName,
        phone,
        specialisation,
        notes,
        consentTaken: true,
      });
    },
    onSuccess: () => {
      toast.success('Referral submitted to hospital CRM');
      setPatientName('');
      setPhone('');
      setNotes('');
      setConsent(false);
      qc.invalidateQueries({ queryKey: ['partner-leads'] });
      qc.invalidateQueries({ queryKey: ['partner-me'] });
      qc.invalidateQueries({ queryKey: ['partner-points'] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to submit');
    },
  });

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-white">Referrals</h2>
        <p className="text-slate-400">Choose a hospital, then submit the patient you are referring</p>
      </div>

      <form
        className="rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-6 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!hospitalId) {
            toast.error('Please select a hospital');
            return;
          }
          if (!consent) {
            toast.error('Patient consent is required');
            return;
          }
          createMut.mutate();
        }}
      >
        <h3 className="font-semibold text-white">New referral</h3>

        <PartnerHospitalSelect
          value={hospitalId}
          onChange={setHospitalId}
          variant="portal"
          disabled={createMut.isPending}
        />

        <Input
          placeholder="Patient name"
          value={patientName}
          onChange={(e) => setPatientName(e.target.value)}
          className="border-white/10 bg-slate-900 text-white h-11"
          required
        />
        <Input
          placeholder="10-digit mobile"
          value={phone}
          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
          className="border-white/10 bg-slate-900 text-white h-11"
          required
        />
        <Input
          placeholder="Specialisation (optional)"
          value={specialisation}
          onChange={(e) => setSpecialisation(e.target.value)}
          className="border-white/10 bg-slate-900 text-white h-11"
        />
        <textarea
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full min-h-[80px] rounded-md border border-white/10 bg-slate-900 px-3 py-2 text-white"
        />
        <label className="flex items-start gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          I confirm the patient has consented to share their details for care coordination.
        </label>
        <Button
          type="submit"
          disabled={createMut.isPending || !hospitalId}
          className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-violet-600"
        >
          {createMut.isPending ? 'Submitting…' : 'Submit referral'}
        </Button>
      </form>

      <div>
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="font-semibold text-white">Your referrals</h3>
          <div className="w-full sm:w-64">
            <label className="text-xs text-slate-500 mb-1 block">Filter by hospital</label>
            <FilterHospitalSelect value={filterHospitalId} onChange={setFilterHospitalId} />
          </div>
        </div>
        {isLoading ? (
          <p className="text-slate-400">Loading…</p>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
              <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-white/5 text-left text-slate-400">
                <tr>
                  <th className="px-4 py-3">Patient</th>
                  <th className="px-4 py-3">Hospital</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Points</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {(leads as Array<{
                  patientName: string;
                  phone: string;
                  status: string;
                  leadSubStatus?: string;
                  hospital?: { name: string };
                  partnerPointsLedger?: { milestone: string; points: number }[];
                }>).map((l, i) => (
                  <tr key={i} className="text-slate-200">
                    <td className="px-4 py-3">
                      <div className="font-medium">{l.patientName}</div>
                      <div className="text-xs text-slate-500">{l.phone}</div>
                    </td>
                    <td className="px-4 py-3">{l.hospital?.name}</td>
                    <td className="px-4 py-3">{l.leadSubStatus || l.status}</td>
                    <td className="px-4 py-3">
                      {(l.partnerPointsLedger ?? [])
                        .map((p) => `${formatMilestone(p.milestone)}: ${p.points}`)
                        .join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            {!leads?.length && (
              <p className="px-4 py-8 text-center text-slate-500">No referrals yet</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatMilestone(m: string) {
  if (m === 'OPD_VISITED') return 'OPD';
  if (m === 'IPD_ADMITTED') return 'IPD';
  if (m === 'NEW') return 'New';
  return m;
}

function FilterHospitalSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { data: hospitals = [] } = useQuery({
    queryKey: ['partner-available-hospitals'],
    queryFn: async () => {
      const res = await referralPartnerApi.availableHospitals();
      return res.data?.data ?? [];
    },
  });

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="border-white/10 bg-slate-900 text-white h-9">
        <SelectValue placeholder="All hospitals" />
      </SelectTrigger>
      <SelectContent className="bg-slate-900 text-white border-white/10">
        <SelectItem value="all">All hospitals</SelectItem>
        {(hospitals as { id: string; name: string }[]).map((row) => (
          <SelectItem key={row.id} value={row.id}>
            {row.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
